import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";

import axios from "axios";
import { environment } from "../../environments/environment";
import { useAuthContext } from "../../hooks/useAuthContext";

const PaymentRedirectPage = () => {
  const { user } = useAuthContext();
  const [paymentStatus, setPaymentStatus] = useState("");
  const [nextPaymentDate, setNextPaymentDate] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const [searchParams] = useSearchParams();

  const upgradePlan = async () => {
    try {
      const success = searchParams.get("success");
      const cancelled = searchParams.get("cancelled");
      const subscription_id = searchParams.get("subscription_id");
      const status = searchParams.get("status");
      const session_id = searchParams.get("session_id");

      if (cancelled === "true") {
        setPaymentStatus("cancelled");
        return;
      }

      const paid =
        (subscription_id && (status === "active" || status === "succeeded")) ||
        (success === "true" && session_id);

      if (paid) {
        const body = subscription_id ? { subscription_id } : { session_id };
        await axios.post(
          `${environment.BACKEND}/user/payment/upgrade-plan`,
          body,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.accessToken}`,
            },
          }
        );

        setPaymentStatus("success");
        const currentDate = new Date();
        const nextMonth = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth() + 1,
          currentDate.getDate()
        );
        setNextPaymentDate(nextMonth.toLocaleDateString());
      } else if (!subscription_id && !session_id) {
        setPaymentStatus("cancelled");
      } else {
        setPaymentStatus("unknown");
      }
    } catch (err) {
      setPaymentStatus("unknown");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    upgradePlan();
  }, []);

  // Loading
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 sm:p-10 max-w-sm w-full text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-100 mb-5">
            <svg
              className="animate-spin h-6 w-6 text-emerald-600"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-slate-900">Setting up your plan</h2>
          <p className="mt-2 text-slate-600 text-sm">This will only take a moment…</p>
        </div>
      </div>
    );
  }

  // Success
  if (paymentStatus === "success") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-12">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 sm:p-10 max-w-md w-full text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 mb-6">
            <svg
              className="h-8 w-8 text-emerald-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">You’re all set</h1>
          <p className="mt-3 text-slate-600">
            Your plan is active. You can use all features right away.
          </p>
          {nextPaymentDate && (
            <p className="mt-2 text-slate-500 text-sm">
              Next billing date: <span className="font-medium text-slate-700">{nextPaymentDate}</span>
            </p>
          )}
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/dashboard"
              className="inline-flex items-center justify-center rounded-xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white hover:bg-emerald-700 transition-colors"
            >
              Go to dashboard
            </Link>
            <Link
              to="/profile"
              className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
            >
              View subscription
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Cancelled
  if (paymentStatus === "cancelled") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-12">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 sm:p-10 max-w-md w-full text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 mb-6">
            <svg
              className="h-8 w-8 text-slate-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Payment cancelled</h1>
          <p className="mt-3 text-slate-600">
            No charge was made. You can try again whenever you’re ready.
          </p>
          <div className="mt-8">
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center rounded-xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800 transition-colors"
            >
              Back to pricing
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Unknown / error
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-12">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 sm:p-10 max-w-md w-full text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 mb-6">
          <svg
            className="h-8 w-8 text-amber-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-slate-900">Something went wrong</h1>
        <p className="mt-3 text-slate-600">
          We couldn’t complete the upgrade. If you were charged, we’ll sort it out—reach out at{" "}
          <a
            href="mailto:support@leadzlab.co"
            className="font-medium text-emerald-600 hover:text-emerald-700 underline"
          >
            support@leadzlab.co
          </a>
          .
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/pricing"
            className="inline-flex items-center justify-center rounded-xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white hover:bg-emerald-700 transition-colors"
          >
            Try again
          </Link>
          <Link
            to="/dashboard"
            className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
          >
            Go to dashboard
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PaymentRedirectPage;
